/**
 * アプリケーションのエントリーポイント
 */

import { UIController } from './controllers/UIController.js';
import { BackendService } from './services/BackendService.js';
import { ErrorHandler } from './utils/ErrorHandler.js';
import { ValidationUtils } from './utils/ValidationUtils.js';
import { AppConfig } from './config.js';
import type { AppState } from './types/app.types.js';

interface ServiceNoticeResponse {
  enabled: boolean;
  message: string;
}

// グローバルエラーハンドリング
window.addEventListener('error', (event) => {
  console.error('Application initialization failed:', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  event.preventDefault();
});

function isServiceNoticeResponse(value: unknown): value is ServiceNoticeResponse {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return typeof candidate.enabled === 'boolean' && typeof candidate.message === 'string';
}

async function updateServiceNotice(): Promise<void> {
  const noticeElement = document.getElementById('serviceNotice');
  const noticeMessageElement = document.getElementById('serviceNoticeMessage');

  if (!noticeElement || !noticeMessageElement) {
    return;
  }

  const hideNotice = (): void => {
    noticeElement.style.display = 'none';
    noticeMessageElement.textContent = '';
  };

  try {
    const response = await fetch('/api/service-notice', {
      cache: 'no-store'
    });

    if (!response.ok) {
      hideNotice();
      return;
    }

    const payload: unknown = await response.json();
    if (!isServiceNoticeResponse(payload)) {
      hideNotice();
      return;
    }

    const message = payload.message.trim();
    if (!payload.enabled || message.length === 0) {
      hideNotice();
      return;
    }

    noticeMessageElement.textContent = message;
    noticeElement.style.display = 'block';
  } catch (error) {
    console.warn('サービス告知の取得に失敗:', error);
    hideNotice();
  }
}

// DOM要素の取得
function getDOMElements(): Record<string, HTMLElement> {
  const elements = {
    // 入力要素
    targetSizeInput: document.getElementById('targetSize'),
    sizeDisplay: document.getElementById('sizeDisplay'),
    inputError: document.getElementById('sizeError'),
    sizeError: document.getElementById('sizeError'), // inputErrorとsizeErrorの両方で参照
    generateBtn: document.getElementById('generateBtn'),
    
    // 進捗表示要素
    progressSection: document.getElementById('progressSection'),
    progressBar: document.getElementById('progressBar'),
    progressText: document.getElementById('progressPercentage'),
    progressPercentage: document.getElementById('progressPercentage'),
    progressPhase: document.getElementById('currentPhase'),
    currentPhase: document.getElementById('currentPhase'),
    progressTime: document.getElementById('estimatedTime'),
    processingMethod: document.getElementById('processingMethod'),
    methodValue: document.getElementById('methodValue'),
    
    // 結果表示要素
    resultSection: document.getElementById('resultSection'),
    resultStats: document.getElementById('targetSizeValue'), // 複数の統計要素の代表
    targetSizeValue: document.getElementById('targetSizeValue'),
    actualSizeValue: document.getElementById('actualSizeValue'),
    processingTimeValue: document.getElementById('processingTimeValue'),
    accuracyBadge: document.getElementById('accuracyBadge'),
    downloadBtn: document.getElementById('downloadBtn'),
    generateAnotherBtn: document.getElementById('generateAnotherBtn'),
    
    // エラー表示要素
    errorSection: document.getElementById('errorSection'),
    errorMessage: document.getElementById('errorMessage'),
    errorDetails: document.getElementById('errorSuggestion'), // errorSuggestionを使用
    errorSuggestions: document.getElementById('errorSuggestion'),
    errorSuggestion: document.getElementById('errorSuggestion'),
    errorCloseBtn: document.getElementById('errorClose'),
    retryBtn: document.getElementById('retryBtn'),
    
    // デバッグ要素
    debugToggle: document.getElementById('debugToggle'),
    debugContent: document.getElementById('debugContent')
  };

  // 必須要素の存在確認
  const criticalElements = [
    'targetSizeInput', 'generateBtn', 'progressSection', 'resultSection', 'errorSection'
  ];

  const missingCriticalElements = criticalElements
    .filter(name => !elements[name as keyof typeof elements])
    .map(name => name);

  if (missingCriticalElements.length > 0) {
    throw new Error(`Missing critical DOM elements: ${missingCriticalElements.join(', ')}`);
  }

  return elements as Record<string, HTMLElement>;
}

// アプリケーション初期化
async function initializeApplication(): Promise<void> {
  try {
    await updateServiceNotice();

    // DOM要素取得
    const elements = getDOMElements();
    
    // 初期状態定義
    const initialState: AppState = {
      input: {
        targetSize: null,
        isValid: false,
        errorMessage: null
      },
      generation: {
        status: 'idle',
        progress: 0,
        currentPhase: null,
        estimatedTime: null
      },
      result: {
        blob: null,
        actualSize: null,
        accuracy: null,
        processingTime: null,
        metadata: null
      },
      error: {
        hasError: false,
        error: null,
        errorHistory: [],
        maxHistorySize: 50
      },
      ui: {
        showDebugInfo: AppConfig.ui.debugMode,
        theme: 'light'
      }
    };
    
    // サービス初期化
    const backendService = new BackendService(AppConfig.backend.baseUrl, AppConfig.backend.timeout);
    const uiController = new UIController(initialState, elements);
    
    // UIイベントハンドラーの設定
    setupEventHandlers(elements, uiController, backendService, initialState);
    
    // 初期UI更新
    uiController.updateUI();
    
  } catch (error) {
    console.error('Failed to initialize application:', error);
    
    // フォールバック: 基本的なエラー表示
    const errorElement = document.getElementById('error-section');
    if (errorElement) {
      errorElement.style.display = 'block';
      const messageElement = document.getElementById('error-message');
      if (messageElement) {
        messageElement.textContent = 'アプリケーションの初期化に失敗しました。ページを再読み込みしてください。';
      }
    }
    
    throw error;
  }
}

// イベントハンドラーの設定
function setupEventHandlers(
  elements: Record<string, HTMLElement>,
  uiController: UIController,
  backendService: BackendService,
  state: AppState
): void {
  const targetSizeInput = elements.targetSizeInput as HTMLInputElement;
  const generateBtn = elements.generateBtn as HTMLButtonElement;
  const downloadBtn = elements.downloadBtn as HTMLButtonElement;
  const generateAnotherBtn = elements.generateAnotherBtn as HTMLButtonElement;
  const errorCloseBtn = elements.errorCloseBtn as HTMLButtonElement;
  const retryBtn = elements.retryBtn as HTMLButtonElement;
  const debugToggle = elements.debugToggle as HTMLButtonElement;
  
  // 入力値変更
  targetSizeInput.addEventListener('input', () => {
    const value = targetSizeInput.value.trim();
    
    if (!value) {
      state.input.targetSize = null;
      state.input.isValid = false;
      state.input.errorMessage = null;
    } else {
      const numValue = parseInt(value, 10);
      const validation = ValidationUtils.validateFileSize(numValue);
      
      state.input.targetSize = validation.isValid ? numValue : null;
      state.input.isValid = validation.isValid;
      state.input.errorMessage = validation.errorMessage;
    }
    
    uiController.updateSizeDisplay(state.input.targetSize || 0);
    uiController.updateInputError(state.input.errorMessage);
    uiController.updateGenerateButton();
  });
  
  // 画像生成
  generateBtn.addEventListener('click', async () => {
    if (!state.input.isValid || !state.input.targetSize) return;
    
    try {
      state.generation.status = 'generating';
      uiController.hideError();
      uiController.updateUI();
      
      // プログレス表示（バックエンド処理のため擬似的）
      state.generation.progress = 10;
      state.generation.currentPhase = 'size-calculation';
      uiController.updateProgress();
      
      const response = await backendService.generateImage(state.input.targetSize);
      
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'バックエンドAPIでエラーが発生しました');
      }
      
      // プログレス更新
      state.generation.progress = 90;
      state.generation.currentPhase = 'binary-padding';
      uiController.updateProgress();
      
      // Blobを作成
      const blob = new Blob([response.data.buffer], { type: 'image/jpeg' });
      
      state.generation.status = 'success';
      state.result.blob = blob;
      state.result.actualSize = response.data.actualSize;
      state.result.accuracy = response.data.accuracy;
      state.result.processingTime = response.data.processingTime;
      state.result.metadata = {
        generatedAt: new Date(),
        targetSize: state.input.targetSize,
        actualSize: response.data.actualSize,
        accuracy: response.data.accuracy,
        processingTime: response.data.processingTime,
        processingMethod: 'backend' as const,
        algorithmPhases: (response.data.metadata.phases || []).map(phase => ({
          phase: phase as 'size-calculation' | 'quality-adjustment' | 'binary-padding',
          startTime: 0,
          endTime: 0,
          iterations: 0,
          result: { size: 0 }
        }))
      };
      
      uiController.updateUI();
      
    } catch (error) {
      state.generation.status = 'error';
      
      const errorHandler = ErrorHandler.getInstance();
      const appError = errorHandler.createUnknownError(error);
      
      state.error.hasError = true;
      state.error.error = appError;
      
      uiController.showError(appError);
    }
  });
  
  // ダウンロード
  downloadBtn.addEventListener('click', () => {
    uiController.handleDownload();
  });
  
  // 新規生成
  generateAnotherBtn.addEventListener('click', () => {
    uiController.handleGenerateAnother();
  });
  
  // エラー閉じる
  errorCloseBtn.addEventListener('click', () => {
    uiController.handleErrorClose();
  });
  
  // リトライ
  retryBtn.addEventListener('click', () => {
    generateBtn.click();
  });
  
  // デバッグ表示切り替え
  debugToggle.addEventListener('click', () => {
    uiController.handleDebugToggle();
  });
}

// アプリケーション開始
document.addEventListener('DOMContentLoaded', () => {
  initializeApplication().catch(error => {
    console.error('Critical application error:', error);
  });
}); 